import React, { useState, useRef, useCallback } from 'react';

const ImageSliderCompare = ({ beforeImage, afterImage, beforeDate, afterDate }) => {
  const [sliderPos, setSliderPos] = useState(50);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  const updateSlider = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPos(pct);
  }, []);

  const handleMouseDown = useCallback(() => { isDragging.current = true; }, []);
  const handleMouseUp = useCallback(() => { isDragging.current = false; }, []);
  const handleMouseMove = useCallback((e) => {
    if (isDragging.current) updateSlider(e.clientX);
  }, [updateSlider]);
  const handleTouchMove = useCallback((e) => {
    updateSlider(e.touches[0].clientX);
  }, [updateSlider]);
  const handleClick = useCallback((e) => {
    updateSlider(e.clientX);
  }, [updateSlider]);

  if (!beforeImage || !afterImage) {
    return (
      <div className="bg-[#FFF2EC] rounded-xl p-8 text-center" data-testid="slider-no-images">
        <p className="text-sm text-[#8C7A76]">Both selfies need images for the overlay comparison.</p>
      </div>
    );
  }

  const beforeSrc = `data:image/jpeg;base64,${beforeImage}`;
  const afterSrc = `data:image/jpeg;base64,${afterImage}`;

  return (
    <div className="space-y-2" data-testid="image-slider-compare">
      <div
        ref={containerRef}
        className="relative w-full aspect-square rounded-xl overflow-hidden cursor-col-resize select-none"
        style={{ maxHeight: '400px' }}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
        onClick={handleClick}
      >
        {/* After image (full) */}
        <img src={afterSrc} alt="After" className="absolute inset-0 w-full h-full object-cover" />

        {/* Before image (clipped) */}
        <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}>
          <img src={beforeSrc} alt="Before" className="w-full h-full object-cover" />
        </div>

        {/* Slider line */}
        <div className="absolute top-0 bottom-0" style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)' }}>
          <div className="w-0.5 h-full bg-white shadow-lg" />
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-xl flex items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M7 4L3 10L7 16" stroke="#FF6B4A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M13 4L17 10L13 16" stroke="#FF6B4A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        {/* Labels */}
        <div className="absolute top-3 left-3 bg-[#1A1514]/70 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full">
          Before
        </div>
        <div className="absolute top-3 right-3 bg-[#4ADE80]/80 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full">
          After
        </div>
      </div>

      {/* Date labels */}
      <div className="flex justify-between text-xs text-[#8C7A76] px-1">
        <span>{beforeDate}</span>
        <span className="text-[#8C7A76]">Drag slider to compare</span>
        <span>{afterDate}</span>
      </div>
    </div>
  );
};

export default ImageSliderCompare;
